
'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function ReviewsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const featuredReviews = [
    {
      name: 'Mike T.',
      age: 34,
      location: 'Arizona',
      rating: 5,
      text: "Had this for 6 months now. Best purchase I've made. Never fails, looks professional, and my cigarettes stay fresh. Worth every penny.",
      image: 'https://ae-pic-a1.aliexpress-media.com/kf/A123a69640d2d4f9f94cff41d2dccd9494.jpg_960x960q75.jpg_.avif',
      verified: true,
    },
    {
      name: 'James R.',
      age: 29,
      location: 'Texas',
      rating: 5,
      text: 'I work construction and this thing takes a beating. Dropped it off a roof, still works perfect. My coworkers are all asking where I got it.',
      image: 'https://ae-pic-a1.aliexpress-media.com/kf/A38cdaaa5533d4475bd2f4c3f3be1329bv.jpg_220x220.jpg_.avif',
      verified: true,
    },
    {
      name: 'David L.',
      age: 41,
      location: 'Florida',
      rating: 5,
      text: 'Finally, a lighter that works in Florida humidity and rain. The cigarette case is genius too. Highly recommend.',
      image: 'https://ae-pic-a1.aliexpress-media.com/kf/Abc3ccb28909d42f487d24bddcff023a6d.jpg_220x220.jpg_.avif',
      verified: true,
    },
    {
      name: 'Chris M.',
      age: 38,
      location: 'New York',
      rating: 5,
      text: 'Looks way more expensive than it costs. Got compliments at the bar last weekend. Functions flawlessly.',
      image: 'https://ae-pic-a1.aliexpress-media.com/kf/A2431e1f9891d4d45a56f2855ff19a74aK.jpg_960x960q75.jpg_.avif',
      verified: true,
    },
    {
      name: 'Tony K.',
      age: 45,
      location: 'California',
      rating: 5,
      text: 'Bought 3 more as gifts. Everyone loves them. Quality is outstanding for the price.',
      image: 'https://ae-pic-a1.aliexpress-media.com/kf/A4415fce95256400aa4ddf04902cec758j.jpg_960x960q75.jpg_.avif',
      verified: true,
    },
    {
      name: 'Sarah J.',
      age: 32,
      location: 'Ohio',
      rating: 4,
      text: "Bought for my husband. He loves it but wishes the case held more cigarettes. Otherwise perfect.",
      image: 'https://static.readdy.ai/image/b66a7d3fce0ec8099d6508e6c0faefc9/81f3f68ebf98b81ff6b016af76effe34.png',
      verified: true,
      hasResponse: true,
      storeResponse: "Thanks Sarah! The compact design balances portability with protection. Glad your husband loves the reliability!"
    },
  ];

  return (
    <section id="reviews" ref={ref} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-6 sm:mb-10 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-4 sm:mb-6">
            What Real Customers Say
          </h2>
          <div className="flex items-center justify-center gap-1 mb-2 sm:mb-3">
            {[...Array(5)].map((_, i) => (
              <motion.i
                key={i}
                initial={{ opacity: 0, rotate: -180 }}
                animate={isInView ? { opacity: 1, rotate: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="ri-star-fill text-lg sm:text-xl md:text-2xl text-yellow-500"
              />
            ))}
          </div>
          <p className="text-black/60 text-xs sm:text-sm md:text-base">
            Based on verified customer reviews
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featuredReviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6"
            >
              {/* Image */}
              <div className="relative aspect-square mb-3 sm:mb-4 md:mb-6 overflow-hidden rounded-lg bg-gray-100">
                <img
                  src={review.image}
                  alt={`Review by ${review.name}`}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-2 sm:mb-3">
                {[...Array(5)].map((_, i) => (
                  <motion.i
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.3, delay: index * 0.1 + i * 0.05 }}
                    className={`ri-star-fill text-xs sm:text-sm md:text-base ${
                      i < review.rating ? 'text-yellow-500' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Review Text */}
              <p className="text-black/80 text-[11px] sm:text-xs md:text-sm mb-3 sm:mb-4 leading-relaxed">
                &quot;{review.text}&quot;
              </p>

              {/* Reviewer Info */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium text-black text-[11px] sm:text-xs md:text-sm">
                    {review.name}, {review.age}
                  </p>
                  <p className="text-black/50 text-[9px] sm:text-[10px] md:text-xs">{review.location}</p>
                </div>
                {review.verified && (
                  <div className="flex items-center gap-1 text-green-600 text-[9px] sm:text-[10px] md:text-xs">
                    <i className="ri-checkbox-circle-fill" />
                    <span>Verified</span>
                  </div>
                )}
              </div>

              {/* Store Response */}
              {review.hasResponse && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                  className="bg-blue-50 border-l-2 border-blue-500 rounded p-2 sm:p-3"
                >
                  <p className="text-[9px] sm:text-[10px] md:text-xs font-medium text-blue-900 mb-1">
                    <i className="ri-store-2-line mr-1" />
                    Store Response:
                  </p>
                  <p className="text-[9px] sm:text-[10px] md:text-xs text-blue-800 leading-relaxed">
                    {review.storeResponse}
                  </p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
